<template>
  <div
    ref="gridRoot"
    class="aggregation-grid"
    :class="{ 'aggregation-grid--loading': loading }"
  >
    <!-- 集計中インジケータ。セル保存 → 依存関係の再計算が走っている間、
         薄い不確定プログレスバーをグリッド上部に表示して「処理中」感を出す。
         セル自体は CSS で薄表示＋ cursor:wait でロックを示す（編集不可）。 -->
    <div
      v-if="loading"
      class="grid-loading-indicator"
      aria-live="polite"
    >
      <v-progress-linear
        indeterminate
        color="primary"
        height="2"
      />
    </div>
    <div class="grid-scroll">
      <table class="grid">
        <thead>
          <tr class="grid-head">
            <th
              v-for="header in headers"
              :key="`grid-head-${header.value}`"
              class="grid-cell grid-head-cell"
              :class="{
                'grid-head-sorted': sortKey === header.value,
                'grid-cell-fixed-col': header.value === 'name',
                'grid-cell-fixed-corner': header.value === 'name',
              }"
              role="button"
              :aria-sort="ariaSort(header.value)"
              @click="toggleSort(header.value)"
            >
              <span class="grid-head-content">
                <!-- 編集可能な BR 列の目印（鉛筆） -->
                <v-icon
                  v-if="header.editable"
                  x-small
                  class="grid-head-editable-icon"
                >
                  {{ icons.mdiPencilOutline }}
                </v-icon>
                <span class="grid-head-text">{{ header.text }}</span>
                <!-- ソート中の列: ▲/▼ をはっきり表示 -->
                <v-icon
                  v-if="sortKey === header.value"
                  x-small
                  class="grid-sort-icon"
                >
                  {{ sortOrder === 'asc' ? icons.mdiArrowUp : icons.mdiArrowDown }}
                </v-icon>
                <!-- 未ソートの列: ホバー時のみ薄い昇順矢印を表示（CSS で opacity 制御） -->
                <v-icon
                  v-else
                  x-small
                  class="grid-sort-icon grid-sort-icon-hint"
                >
                  {{ icons.mdiArrowUp }}
                </v-icon>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, rowIdx) in sortedItems"
            :key="`grid-row-${item.contextType}-${item.id || rowIdx}`"
          >
            <td
              v-for="header in headers"
              :key="`grid-cell-${item.id || rowIdx}-${header.value}`"
              class="grid-cell"
              :class="cellClasses(item, header)"
              @click="onCellClick(item, header)"
            >
              <!-- 行見出し（ユーザー名など）。
                   ユーザー集計のときは販売実績パネルを開くためクリック可能にする。 -->
              <template v-if="header.value === 'name'">
                <span
                  v-if="isUserAggregation"
                  class="grid-user-name-link"
                  role="button"
                  tabindex="0"
                  @click.stop="$emit('select-user', { userId: item.id, userName: item.name })"
                  @keydown.enter.stop="$emit('select-user', { userId: item.id, userName: item.name })"
                >
                  {{ item.name }}
                </span>
                <template v-else>
                  {{ item.name }}
                </template>
              </template>

              <!-- boolean セル: クリックでトグル（チェックボックス表示） -->
              <template v-else-if="isBooleanCell(item, header.value)">
                <v-icon
                  small
                  :color="effectiveValue(item, header.value) ? 'primary' : ''"
                >
                  {{ effectiveValue(item, header.value) ? icons.mdiCheckboxMarked : icons.mdiCheckboxBlankOutline }}
                </v-icon>
              </template>

              <!-- 編集中の number セル: セル内に直接数値入力欄を表示 -->
              <input
                v-else-if="isEditingCell(item, header.value) && isNumberCell(item, header.value)"
                :ref="editingInputRef"
                v-model="editingValue"
                v-format-number-on-blur
                class="grid-cell-input grid-cell-input-number"
                type="number"
                inputmode="numeric"
                @blur="commitEdit"
                @keydown.enter="commitEditAndBlur"
                @keydown.esc.prevent="cancelEdit"
                @wheel.prevent="$event.target.blur()"
              >

              <!-- 編集中の text セル: セル内に直接テキスト入力欄を表示 -->
              <input
                v-else-if="isEditingCell(item, header.value)"
                :ref="editingInputRef"
                v-model="editingValue"
                class="grid-cell-input"
                type="text"
                @blur="commitEdit"
                @keydown.enter="commitEditAndBlur"
                @keydown.esc.prevent="cancelEdit"
              >

              <!-- 通常表示の text / number セル -->
              <template v-else>
                {{ displayCellValue(item, header.value) }}
              </template>
            </td>
          </tr>

          <tr v-if="items.length === 0">
            <td
              :colspan="headers.length || 1"
              class="grid-cell grid-empty"
            >
              データがありません
            </td>
          </tr>
        </tbody>

        <!-- 合計行（数値列の合計）。店舗集計は 1 行だけの複製になるため出さない -->
        <tfoot v-if="items.length > 0 && showSumRow">
          <tr>
            <td
              v-for="header in headers"
              :key="`grid-sum-${header.value}`"
              class="grid-cell grid-sum-cell"
              :class="{
                'grid-cell-number': header.value !== 'name',
                'grid-cell-fixed-col': header.value === 'name',
              }"
            >
              {{ sumCellText(header) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  ref, computed, watch, onUnmounted, nextTick,
} from '@vue/composition-api'
import {
  mdiCheckboxMarked, mdiCheckboxBlankOutline, mdiArrowUp, mdiArrowDown, mdiPencilOutline,
} from '@mdi/js'
import { debounce } from 'lodash'
import { judgeType, interpretValue } from '@/utils/typeUtils'
import formatNumberOnBlur from '@/directives/formatNumberOnBlur'

// 保存リクエスト（emit save → API）のデバウンス間隔。
// 注意: デバウンスは「保存リクエスト」だけに効かせる。表示の楽観的更新は
// commitEdit 時に即座に反映する（debounce で遅らせない）。
const SAVE_DEBOUNCE_MS = 500

// 楽観的更新ストア（モジュールスコープ）。
// 集計データの再取得などで AggregationGrid が一旦アンマウント→再マウント
// されても入力値が失われないよう、コンポーネントの setup スコープ外に置く。
// キーは `aggregationId::contextType-contextId-businessRuleId` で集計表ごとに
// 衝突しない。Vue.observable でリアクティブにし、表示へ即時反映させる。
const optimisticOverrides = Vue.observable({ map: {} })

/**
 * 新クロージングページ専用の集計表グリッド。
 *
 * 既存 `eod-closing/components/AggregationTable.vue` および `useAggregationTable` の
 * ロジック（ヘッダー/行の組み立て、セル種別判定、未保存判定）を参考に、
 * Vuetify v-data-table に依存しないネイティブ table で再実装している。
 * セル編集は Excel のようなインライン編集（セル内に直接入力欄を表示）。
 * モック closing-page.html のスプレッドシート（全セル罫線の格子）に忠実な見た目とする。
 */
export default {
  name: 'AggregationGrid',
  directives: {
    formatNumberOnBlur,
  },
  props: {
    // 集計表（JSON:API 構造の attributes を持つオブジェクト）
    aggregation: {
      type: Object,
      default: () => ({
        attributes: {
          id: null,
          name: '',
          type: '',
          businessRules: { data: [] },
          contexts: { data: [] },
        },
      }),
    },
    // セル操作のロック（保存中・集計中など）
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // --- 集計表タイプ判定 -------------------------------------------------
    const isUserAggregation = computed(() => (
      /(UserAggregation|PayrollAggregation)$/.test(props.aggregation.attributes.type || '')
    ))

    // 店舗集計はコンテキストが店舗 1 行だけのため、合計行が行の複製にしかならず不要。
    const showSumRow = computed(() => (
      (props.aggregation.attributes.type || '') !== 'EodClosingClubAggregation'
    ))

    // --- 列（ビジネスルール）単位の編集可否 -----------------------------
    // businessRuleId -> その列が編集可能か（boolean）。
    // editable はセル(result)単位だが editable_result は BR 単位のフラグなので、
    // ある BR でいずれか 1 つでも editable な result があれば、その列は編集可能。
    const editableColumns = computed(() => {
      const map = {}
      const contexts = props.aggregation.attributes.contexts?.data || []
      contexts.forEach(context => {
        const resultsData = context.attributes?.results?.data || []
        resultsData.forEach(result => {
          const { businessRuleId, editable } = result.attributes || {}
          if (businessRuleId === null || businessRuleId === undefined) return
          if (editable) map[businessRuleId] = true
        })
      })

      return map
    })

    // --- ヘッダー組み立て ------------------------------------------------
    // ビジネスルール列。ユーザー集計のときは先頭に「ユーザー」見出し列を足す。
    const headers = computed(() => {
      const businessRules = props.aggregation.attributes.businessRules?.data || []
      const ruleHeaders = businessRules.map(rule => ({
        text: rule.attributes.name,
        value: rule.id,
        editable: !!editableColumns.value[rule.id],
      }))

      if (isUserAggregation.value) {
        return [{ text: 'ユーザー', value: 'name' }, ...ruleHeaders]
      }

      return ruleHeaders
    })

    // --- 行（item）組み立て ---------------------------------------------
    // 1 context = 1 行。businessRuleId をキーに値・メタ情報を持たせる。
    const buildItem = context => {
      const {
        contextType, id: contextId, name: contextName, results,
      } = context.attributes

      const item = {
        contextType,
        id: contextId,
        name: contextName,
        values: {},
        meta: {
          businessRuleResultIds: {},
          editable: {},
          inputTypes: {},
          originalValues: {},
        },
      }

      const resultsData = results?.data || []
      resultsData.forEach(result => {
        const {
          businessRuleId, businessRuleResultId, value, editable,
        } = result.attributes

        // 配列・オブジェクト値は表セルに載せない（既存 AggregationTable と同じ扱い）
        if (Array.isArray(value)) return
        if (value !== null && typeof value === 'object') return

        const interpreted = interpretValue(value)
        item.values[businessRuleId] = interpreted
        item.meta.businessRuleResultIds[businessRuleId] = businessRuleResultId
        item.meta.editable[businessRuleId] = editable
        item.meta.inputTypes[businessRuleId] = judgeType(value)
        item.meta.originalValues[businessRuleId] = interpreted
      })

      return item
    }

    const items = computed(() => (
      (props.aggregation.attributes.contexts?.data || []).map(buildItem)
    ))

    // --- 列（ビジネスルール）単位の値型判定 -----------------------------
    // 共有の judgeType は値が空文字のとき Number('') === 0 のため 'number' を
    // 返してしまう。その結果、空セルを持つ text 列が number 列と誤判定され、
    // type="number" の input になり、文字列を入力できない（数値入力欄に
    // 弾かれ、v-format-number-on-blur で 0 にされる）バグが起きていた。
    //
    // 対策として、セル単体ではなく「列全体の実値」から型を決める。
    // 1 つでも非数値の文字列があれば text、boolean があれば boolean、
    // それ以外で数値があれば number、値が一切無ければ text（既定）。
    // 空値（'' / null / undefined）は型判定に寄与させない。

    // 単一値の型を 'empty' / 'boolean' / 'number' / 'text' に分類する。
    const classifyValue = value => {
      if (value === null || value === undefined || value === '') return 'empty'
      if (typeof value === 'boolean') return 'boolean'
      const str = `${value}`.trim()
      if (str === '') return 'empty'
      if (['true', 'false'].includes(str.toLowerCase())) return 'boolean'
      if (!Number.isNaN(Number(str))) return 'number'

      return 'text'
    }

    // businessRuleId -> 列の型（'boolean' | 'number' | 'text'）
    const columnTypes = computed(() => {
      const types = {}
      const contexts = props.aggregation.attributes.contexts?.data || []
      contexts.forEach(context => {
        const resultsData = context.attributes?.results?.data || []
        resultsData.forEach(result => {
          const attrs = result.attributes || {}
          const { businessRuleId, value } = attrs
          if (businessRuleId === null || businessRuleId === undefined) return
          if (Array.isArray(value)) return
          if (value !== null && typeof value === 'object') return

          const kind = classifyValue(value)
          if (kind === 'empty') return

          const current = types[businessRuleId]
          // 優先度: text > boolean > number（text を最優先で確定させる）
          if (kind === 'text') {
            types[businessRuleId] = 'text'
          } else if (kind === 'boolean') {
            if (current !== 'text') types[businessRuleId] = 'boolean'
          } else if (kind === 'number') {
            if (current !== 'text' && current !== 'boolean') types[businessRuleId] = 'number'
          }
        })
      })

      return types
    })

    // セルの型を返す。列全体で型が確定していればそれを、無ければ 'text'
    // （text input は値を強制変換しないため、未知の場合の安全側）。
    const cellType = businessRuleId => columnTypes.value[businessRuleId] || 'text'

    // --- 楽観的更新（保存値の即時反映）---------------------------------
    // セル単位の識別キー（contextType-contextId-businessRuleId）。
    const cellKey = (item, businessRuleId) => (
      `${item.contextType}-${item.id || 'null'}-${businessRuleId}`
    )

    // モジュールスコープの楽観的更新ストア用キー（集計表ごとに衝突しない）。
    const overrideKey = (item, businessRuleId) => (
      `${props.aggregation.attributes.id}::${cellKey(item, businessRuleId)}`
    )

    // 楽観的更新値を書き込む（commitEdit / boolean トグル時に即座に呼ぶ）。
    const setOverride = (item, businessRuleId, value) => {
      Vue.set(optimisticOverrides.map, overrideKey(item, businessRuleId), value)
    }

    // セルの実効値（楽観更新があればそれを優先、なければ集計データの値）。
    // items は computed 由来の派生オブジェクトで直接書き換えてもリアクティブに
    // 反映されないため、モジュールスコープのリアクティブストアで上書き表示する。
    const effectiveValue = (item, businessRuleId) => {
      const key = overrideKey(item, businessRuleId)
      if (Object.prototype.hasOwnProperty.call(optimisticOverrides.map, key)) {
        return optimisticOverrides.map[key]
      }

      return item.values[businessRuleId]
    }

    // --- 列ソート -------------------------------------------------------
    // 既存 AggregationTable.vue（v-data-table）のソート挙動に準拠。
    // 列ヘッダークリックで「昇順 → 降順 → ソート解除（元の順）」をトグルする。
    const sortKey = ref(null)
    const sortOrder = ref('asc')

    const toggleSort = key => {
      if (sortKey.value !== key) {
        // 別の列をクリック: その列の昇順ソート
        sortKey.value = key
        sortOrder.value = 'asc'

        return
      }
      if (sortOrder.value === 'asc') {
        // 昇順 → 降順
        sortOrder.value = 'desc'

        return
      }
      // 降順 → ソート解除（元の順）
      sortKey.value = null
      sortOrder.value = 'asc'
    }

    const ariaSort = key => {
      if (sortKey.value !== key) return 'none'

      return sortOrder.value === 'asc' ? 'ascending' : 'descending'
    }

    // ソート対象セルの生値を取り出す（name 列は行見出し、それ以外は
    // 楽観更新を反映した実効値）
    const sortRawValue = (item, key) => (
      key === 'name' ? item.name : effectiveValue(item, key)
    )

    // 2値を比較する。数値・真偽値・文字列を適切に扱い、空値は末尾に寄せる。
    const compareValues = (a, b) => {
      const aEmpty = a === null || a === undefined || a === ''
      const bEmpty = b === null || b === undefined || b === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1

      // boolean: false < true
      if (typeof a === 'boolean' || typeof b === 'boolean') {
        return (a ? 1 : 0) - (b ? 1 : 0)
      }

      // number: 両方が数値として解釈できれば数値比較
      const aNum = Number(a)
      const bNum = Number(b)
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return aNum - bNum
      }

      // それ以外は文字列比較（日本語ロケール）
      return String(a).localeCompare(String(b), 'ja')
    }

    // 表示用の行リスト。ソート未指定なら元の順、指定時は安定ソートする。
    const sortedItems = computed(() => {
      if (!sortKey.value) return items.value

      const key = sortKey.value
      const dir = sortOrder.value === 'desc' ? -1 : 1

      // index を添えて安定ソート（同値は元の順を維持）
      return items.value
        .map((item, index) => ({ item, index }))
        .sort((x, y) => {
          const cmp = compareValues(sortRawValue(x.item, key), sortRawValue(y.item, key))
          if (cmp !== 0) return cmp * dir

          return x.index - y.index
        })
        .map(entry => entry.item)
    })

    // --- セル種別・状態判定 ---------------------------------------------
    // 型判定は列全体から決めた cellType を使う（空セルの誤判定を防ぐ）。
    // 第1引数 item は呼び出し側の互換のため受け取るが使用しない。
    const isBooleanCell = (item, businessRuleId) => (
      cellType(businessRuleId) === 'boolean'
    )
    const isNumberCell = (item, businessRuleId) => (
      cellType(businessRuleId) === 'number'
    )
    // 未保存セル: 値はあるが businessRuleResultId が無い（＝まだ保存されていない）
    const isUnsavedCell = (item, businessRuleId) => (
      !item.meta.businessRuleResultIds[businessRuleId]
      && item.values[businessRuleId] !== null
      && item.values[businessRuleId] !== undefined
    )
    // 集計中（props.loading）はセルを編集不可（ロック）にする。
    // ただしロックするのはセル操作のみで、ステップ／画面遷移はブロックしない。
    // 集計中であることはページ上部のグローバルインジケータでも表示される。
    const isEditableCell = (item, businessRuleId) => (
      !props.loading && !!item.meta.editable[businessRuleId]
    )

    // --- インラインセル編集 ---------------------------------------------
    // 編集中セルの識別キー（contextType-contextId-businessRuleId）と編集値。
    const editingCellKey = ref(null)
    const editingValue = ref('')
    let editingContext = null // { item, businessRuleId, isNumber, originalValue }

    const displayCellValue = (item, businessRuleId) => {
      const value = effectiveValue(item, businessRuleId)
      if (value === null || value === undefined) return ''
      if (isNumberCell(item, businessRuleId)) {
        return Number(value).toLocaleString('ja-JP')
      }

      return value
    }

    const isEditingCell = (item, businessRuleId) => (
      editingCellKey.value === cellKey(item, businessRuleId)
    )

    // グリッドのルート要素への参照（編集中 input を DOM 探索するために使う）。
    const gridRoot = ref(null)

    // 編集中 input への参照。
    // :ref に関数を渡して取得するが、v-for + v-if 切り替えでの関数 ref は
    // 呼ばれるタイミングが不安定なため、フォーカスでは「関数 ref で取れた要素」
    // を第一候補にしつつ、取れない場合は DOM から直接探索する（下記参照）。
    // ノード除去時は null で呼ばれるため、null では上書きしない。
    const editingInputEl = ref(null)
    const editingInputRef = el => {
      if (el) editingInputEl.value = el
    }

    // 編集中の input 要素を取得する。
    // 関数 ref で取れていればそれを使い、取れていなければ（v-for 内関数 ref の
    // 取りこぼし対策）グリッド DOM から `.grid-cell-input` を直接探す。
    // 編集中セルは常に 1 つだけなので、該当 input も 1 つに定まる。
    const resolveEditingInput = () => {
      const refEl = editingInputEl.value
      if (refEl && document.body.contains(refEl)) return refEl

      const rootEl = gridRoot.value
      if (rootEl && typeof rootEl.querySelector === 'function') {
        return rootEl.querySelector('.grid-cell-input')
      }

      return null
    }

    // 編集中 input に確実にフォーカスする。
    //
    // 編集モードへ切り替えた直後は、nextTick だけだとブラウザのレイアウト/
    // ペイントが間に合わずワンクリックでフォーカスが当たらないことがある。
    // また v-for 内の関数 ref は呼ばれるタイミングが不安定。
    // そこで nextTick の後、requestAnimationFrame と setTimeout(0) で複数回
    // フォーカスを試み、DOM 探索フォールバックも併用して確実にフォーカスする。
    const focusEditingInput = async targetCellKey => {
      await nextTick()

      const apply = () => {
        // 編集対象が変わっていたら（別セルへ移った/編集終了）何もしない。
        if (targetCellKey && editingCellKey.value !== targetCellKey) return true
        const el = resolveEditingInput()
        if (!el || typeof el.focus !== 'function') return false
        // 既にフォーカス済みなら何もしない（再 focus による副作用回避）。
        // select() は呼ばない。値を全選択（青ハイライト）せず、既存の値を
        // 残したままキャレット（入力カーソル）を表示させるため。
        if (document.activeElement !== el) {
          el.focus()
        }

        return document.activeElement === el
      }

      // 1 回目: 現在のティックで試す
      if (apply()) return
      // 2 回目: 次フレーム（レイアウト確定後）
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(() => {
          if (apply()) return
          // 3 回目: マクロタスクでの最終リトライ
          window.setTimeout(apply, 0)
        })
      } else {
        window.setTimeout(apply, 0)
      }
    }

    // 保存ペイロードを組み立てて emit する（既存と同形）。
    const emitSave = (item, businessRuleId, resultValue) => {
      emit('save', {
        aggregationId: props.aggregation.attributes.id,
        contextId: item.id,
        contextType: item.contextType,
        businessRuleId,
        resultValue,
      })
    }

    // セルごとのデバウンス済み保存関数。
    //
    // 1 つの debounce を全セルで共有すると、別セルを 500ms 以内に続けて
    // 保存したとき lastArgs が後勝ちで上書きされ、先のセルの保存値
    // (や同一セルの正しい値) が失われる。セル単位に debounce を分けることで、
    // 各保存が独立し、引数で渡したキャプチャ済みの値が確実に送られる。
    // デバウンス保存の発行待ちセル（commitEdit 済みだが emitSave 未発行）。
    // 編集確定〜保存リクエスト発行までの隙間に一括保存されないよう busy 判定に使う。
    const pendingSaveKeys = ref(new Set())

    const cellSavers = new Map()
    const getCellSaver = key => {
      if (!cellSavers.has(key)) {
        // 保存リクエスト（emit save → API）のみデバウンスする。
        // 表示の楽観的更新は debounce の外で即座に行う。
        // 値は引数（プリミティブ）で渡され、debounce 後も再参照されない。
        cellSavers.set(key, debounce((item, businessRuleId, resultValue) => {
          emitSave(item, businessRuleId, resultValue)
          // 保存リクエスト発行済み → 発行待ちから外す
          if (pendingSaveKeys.value.has(key)) {
            const next = new Set(pendingSaveKeys.value)
            next.delete(key)
            pendingSaveKeys.value = next
          }
        }, SAVE_DEBOUNCE_MS))
      }

      return cellSavers.get(key)
    }

    // 値を正規化して保存する。rawValue は呼び出し元で「確定時にキャプチャ済み」の値であること。
    // 数値セルを空にして保存した場合は「手入力のクリア」とみなし null を送る（自動計算値に戻す）。
    // 空文字 '' を送るとバックエンドの数値バリデーション（BigDecim('') 失敗）で
    // 「Result valueは不正な値です」エラーになるため、必ず null に正規化する。
    const saveCellValue = (item, businessRuleId, isNumber, rawValue) => {
      let resultValue = rawValue
      if (isNumber) {
        resultValue = (resultValue === null || resultValue === undefined || resultValue === '')
          ? null
          : Number(resultValue)
      }
      // 楽観的更新を即座に反映（表示はここで確定。debounce で遅らせない）。
      setOverride(item, businessRuleId, resultValue)
      // 保存リクエスト発行待ちに登録（debounce 後の emitSave で解除）。
      const key = cellKey(item, businessRuleId)
      if (!pendingSaveKeys.value.has(key)) {
        const next = new Set(pendingSaveKeys.value)
        next.add(key)
        pendingSaveKeys.value = next
      }
      // 保存リクエストだけデバウンスする（セル単位の debounce）。
      // resultValue はプリミティブ値として引数で渡し、クロージャに閉じ込める。
      getCellSaver(key)(item, businessRuleId, resultValue)
    }

    // 編集を確定（blur 時）。値が実際に変わったときのみ保存する。
    const commitEdit = () => {
      if (!editingContext) return
      const {
        item, businessRuleId, isNumber, originalValue,
      } = editingContext

      // 確定時点の入力値を即座にローカル変数へキャプチャする。
      // 以降の保存（debounce 含む）はこの capturedValue のみを使い、
      // editingValue（ref）を後から遅延参照しない。
      const capturedValue = editingValue.value

      let nextValue = capturedValue
      if (isNumber && nextValue !== '' && nextValue !== null && nextValue !== undefined) {
        nextValue = Number(nextValue)
      }

      // 変更がなければ保存しない
      const changed = String(nextValue ?? '') !== String(originalValue ?? '')
      if (changed) {
        saveCellValue(item, businessRuleId, isNumber, capturedValue)
      }

      editingCellKey.value = null
      editingContext = null
    }

    // Enter: 確定して input から抜ける（blur が commitEdit を呼ぶ）。
    //
    // IME（日本語入力）の変換確定の Enter は無視する。
    // 「アルバイト」などカタカナ入力時、変換を確定する Enter は
    // compositionend より前に keydown.enter として届く。これをセル確定と
    // 誤認すると、editingValue がまだ変換前（古い値/空文字）のまま commit され、
    // 「保存されない」「空文字で保存される」不具合になる。
    // event.isComposing（または keyCode 229）で IME 変換中を判定して return し、
    // IME 確定後に改めて押された Enter でのみセルを確定する。
    //
    // テンプレート側は @keydown.enter（.prevent 無し）。IME 変換確定の Enter で
    // preventDefault すると変換確定を妨げるため、preventDefault は
    // 実際にセル確定するときだけ呼ぶ。
    const commitEditAndBlur = event => {
      if (event && (event.isComposing || event.keyCode === 229)) {
        return
      }
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault()
      }
      const el = resolveEditingInput()
      if (el && typeof el.blur === 'function') {
        el.blur()
      } else {
        commitEdit()
      }
    }

    // Esc: 編集をキャンセルし、値を元に戻す
    const cancelEdit = () => {
      editingContext = null
      editingCellKey.value = null
    }

    // text / number セルを編集モードにする共通処理。
    const startEditing = async (item, businessRuleId) => {
      // 既に同じセルを編集中なら何もしない
      if (isEditingCell(item, businessRuleId)) return

      // 別セルを編集中だった場合、先に確定する（変更があれば保存）
      if (editingContext) commitEdit()

      const isNumber = isNumberCell(item, businessRuleId)
      const originalValue = effectiveValue(item, businessRuleId)
      editingContext = {
        item, businessRuleId, isNumber, originalValue,
      }
      editingValue.value = originalValue === null || originalValue === undefined
        ? ''
        : String(originalValue)
      // 直前の編集 input の参照をクリアし、新しい input の関数 ref または
      // DOM 探索で取り直せるようにする。
      editingInputEl.value = null
      const targetCellKey = cellKey(item, businessRuleId)
      editingCellKey.value = targetCellKey

      // input が描画されたらフォーカスして全選択する（ワンクリック編集）。
      await focusEditingInput(targetCellKey)
    }

    // セルクリック時の入口処理。
    //  - boolean セル: クリックでトグル
    //  - text / number セル: ワンクリックで編集モードにする
    const onCellClick = (item, header) => {
      if (header.value === 'name') return
      const businessRuleId = header.value
      if (!isEditableCell(item, businessRuleId)) return

      // boolean セル: クリックでトグル
      if (isBooleanCell(item, businessRuleId)) {
        const current = !!effectiveValue(item, businessRuleId)
        const next = !current
        saveCellValue(item, businessRuleId, false, next)

        return
      }

      startEditing(item, businessRuleId)
    }

    const cellClasses = (item, header) => {
      if (header.value === 'name') {
        // name 列はボディセルとして左端固定する
        return ['grid-row-head', 'grid-cell-fixed-col']
      }

      return {
        'grid-cell-editable': isEditableCell(item, header.value),
        'grid-cell-unsaved': isUnsavedCell(item, header.value),
        'grid-cell-number': isNumberCell(item, header.value),
        'grid-cell-editing': isEditingCell(item, header.value),
      }
    }

    // 集計表が切り替わったらソート状態・編集状態をリセットする。
    // 楽観的更新ストア（optimisticOverrides）はモジュールスコープで集計表ごとに
    // キー分離しているため、ここではクリアしない（再取得や再マウントでも
    // 入力値を保持し続ける。サーバ値が追いついた分は下記 watch で掃除する）。
    watch(() => props.aggregation.attributes.id, () => {
      sortKey.value = null
      sortOrder.value = 'asc'
      editingCellKey.value = null
      editingContext = null
    })

    // 集計データが更新され、サーバ値が楽観的更新値に追いついたら、その
    // override は不要になるため掃除する（サーバの新しい値で正しく更新
    // されるべきセルを、古い override がいつまでも隠さないようにする）。
    watch(items, newItems => {
      newItems.forEach(item => {
        Object.keys(item.values).forEach(brId => {
          const key = overrideKey(item, brId)
          if (!Object.prototype.hasOwnProperty.call(optimisticOverrides.map, key)) return
          const serverValue = item.values[brId]
          const overrideValue = optimisticOverrides.map[key]
          // サーバ値と override が一致したら override を破棄する
          if (String(serverValue ?? '') === String(overrideValue ?? '')) {
            Vue.delete(optimisticOverrides.map, key)
          }
        })
      })
    })

    // 編集中、またはデバウンス保存の発行待ちがある間は「busy」。
    // この間は親（保存ボタン）で一括保存を抑止し、セルの依存関係の
    // 再計算が終わる前に保存が走ってデータ不整合を起こすのを防ぐ。
    const isBusy = computed(() => (
      editingCellKey.value !== null || pendingSaveKeys.value.size > 0
    ))
    watch(isBusy, busy => emit('busy-change', busy), { immediate: true })

    onUnmounted(() => {
      // 保留中のデバウンス保存があれば、破棄せず即時実行して取りこぼさない。
      // （集計データ再取得などで一時的にアンマウントされても保存を失わない）
      cellSavers.forEach(saver => saver.flush())
    })

    // --- 合計行 ---------------------------------------------------------
    // 既存 AggregationTable.vue の accRow を参考に、各数値列の合計を算出する。
    // 行見出し列（name）は「合計」ラベル、数値化できない列は空にする。
    const accRow = computed(() => {
      const sums = {}
      headers.value.forEach(header => {
        if (header.value === 'name') {
          sums[header.value] = '合計'

          return
        }

        let hasNumeric = false
        const total = items.value.reduce((acc, item) => {
          const val = effectiveValue(item, header.value)
          if (val === null || val === undefined || val === '') return acc
          // boolean 値は合計対象外（Number(false)=0/Number(true)=1 で
          // 誤って数値扱いされ、boolean 列に意味のない合計が出るのを防ぐ）
          if (typeof val === 'boolean') return acc
          const numVal = Number(val)
          if (Number.isNaN(numVal)) return acc
          hasNumeric = true

          return acc + numVal
        }, 0)
        sums[header.value] = hasNumeric ? total : ''
      })

      return sums
    })

    const sumCellText = header => {
      const val = accRow.value[header.value]
      if (val === '' || val === null || val === undefined) return val
      if (header.value === 'name') return val

      return Number(val).toLocaleString('ja-JP')
    }

    // クリップボードコピー用のTSV文字列を組み立てる（ヘッダー＋データ行＋合計行）。
    // データ行は現在の表示順（ソート適用後）に従う。
    // 親（DailyAggregationStep）の ⋮ メニューから template ref 経由で呼ばれる。
    const buildClipboardText = () => {
      const headerRow = headers.value.map(h => h.text).join('\t')
      const dataRows = sortedItems.value.map(item => (
        headers.value.map(h => {
          if (h.value === 'name') return item.name
          const val = effectiveValue(item, h.value)

          return val === null || val === undefined ? '' : val
        }).join('\t')
      ))
      // 合計行は表示と同じ条件（店舗集計は出さない）でコピーにも含めない。
      if (!showSumRow.value) return [headerRow, ...dataRows].join('\n')

      const sumRow = headers.value.map(h => {
        const val = accRow.value[h.value]

        return val === null || val === undefined ? '' : val
      }).join('\t')

      return [headerRow, ...dataRows, sumRow].join('\n')
    }

    return {
      gridRoot,
      headers,
      items,
      showSumRow,
      sortedItems,
      sortKey,
      sortOrder,
      toggleSort,
      ariaSort,
      isUserAggregation,
      isBooleanCell,
      isNumberCell,
      isEditableCell,
      effectiveValue,
      displayCellValue,
      cellClasses,
      sumCellText,
      buildClipboardText,
      onCellClick,
      isEditingCell,
      editingValue,
      editingInputRef,
      commitEdit,
      commitEditAndBlur,
      cancelEdit,
      icons: {
        mdiCheckboxMarked,
        mdiCheckboxBlankOutline,
        mdiArrowUp,
        mdiArrowDown,
        mdiPencilOutline,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.aggregation-grid {
  position: relative;
}

// 縦横スクロール用コンテナ。
//
// max-height で縦スクロールコンテナにすることで、ヘッダー行の sticky 固定
// （.grid-head-cell の position: sticky; top: 0）が機能する。
// 縦横スクロールは可能なまま、スクロールバーの表示だけ消すことで、
// バー領域分の余白（過去に出ていた右余白）を出さない。
// 縦スクロールはマウスホイール／トラックパッドで行う。
.grid-scroll {
  overflow: auto;
  max-height: 60vh;

  // 縦横スクロール機能は残しつつ、スクロールバーの表示だけ消す。
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
}

// スプレッドシート: 全セル罫線の格子。
//
// width: 100% でテーブルを常にスクロールコンテナ幅いっぱいにする。
//  - 列が少なく内容が狭いとき → ブラウザが列を広げて埋め、右端に余白が出ない。
//  - 列が多く内容が広いとき → セルが white-space: nowrap で縮まないため
//    テーブルは 100% を超えて広がり、横スクロールで見られる。
table.grid {
  border-collapse: collapse;
  width: 100%;
  table-layout: auto;
  font-size: 13px;
}

.grid-cell {
  border: 1px solid rgba(0, 0, 0, .16);
  padding: 7px 16px;
  white-space: nowrap;
  text-align: right;
  min-width: 120px;
  // セル幅をヘッダー/データ/合計で確実に揃えるため box-sizing を明示
  box-sizing: border-box;
}

// ダークテーマ（Materio ダーク）で視認できる罫線色
.theme--dark .grid-cell {
  border-color: rgba(231, 227, 252, .16);
}

.grid-head-cell {
  text-align: center;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  // sticky 固定時に透けないよう不透明な見出し背景にする
  background: #f4f5fa;

  &:hover {
    background: #e7e8f0;
  }
}

.theme--dark .grid-head-cell {
  background: #3a3552;

  &:hover {
    background: #443e60;
  }
}

// ソート中の列ヘッダーを強調（sticky で透けないよう不透明色）
.grid-head-sorted,
.grid-head-sorted:hover {
  background: #e4d8fc;
}

.theme--dark .grid-head-sorted,
.theme--dark .grid-head-sorted:hover {
  background: #4a3d6b;
}

// ヘッダーのラベルとソートアイコンの並び
.grid-head-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.grid-sort-icon {
  color: var(--v-primary-base, #9155fd) !important;
}

// 編集可能な BR 列の目印（鉛筆）アイコン
.grid-head-editable-icon {
  color: var(--v-primary-base, #9155fd) !important;
}

// 未ソート列のソートヒント矢印: 通常時は非表示、ヘッダーホバー時のみ薄く表示
.grid-sort-icon-hint {
  opacity: 0;
  transition: opacity .15s ease;
}

.grid-head-cell:hover .grid-sort-icon-hint {
  opacity: .5;
}

// 行見出し（ユーザー名など）は左寄せ・固定的な装い。
// 左端固定で透けないよう不透明な背景にする。
// 幅は .grid-cell-fixed-col で固定するため、長い名前は省略表示にする。
.grid-row-head {
  text-align: left;
  font-weight: 600;
  background: #f4f5fa;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .grid-row-head {
  background: #3a3552;
}

// ユーザー集計のユーザー名: クリックで販売実績パネルを開ける。
// 旧クロージングの v-btn と同じく、ホバーでボタン風の背景を表示する。
.grid-user-name-link {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color .15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, .06);
  }
}

.theme--dark .grid-user-name-link:hover {
  background-color: rgba(231, 227, 252, .1);
}

// 合計行。name 列は左端固定されるため、半透明だと透ける。
// 不透明な合計行背景にする。
.grid-sum-cell {
  font-weight: 700;
  background: #eceef5;
}

.theme--dark .grid-sum-cell {
  background: #383350;
}

// ===== ウィンドウ枠固定（Excel のような sticky ヘッダー / 列） =====
// sticky セルはスクロール時に下の内容が透けないよう、背景を不透明にする
// （背景色は上の .grid-head-cell / .grid-row-head / .grid-sum-cell 各規則で
//  不透明色を指定済み。ここでは位置と重なり順のみ定義する）。
// 既存 eod-closing/AggregationTable.vue の fixed-cell（position: sticky）を参考。
//
// z-index の重なり順（高い順）:
//   交差セル(4) > ヘッダー行(3) > name 列(2) > 通常セル(既定)

// ヘッダー行: 縦スクロールで上端に固定
.grid-head-cell {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 3;
}

// name 列: 横スクロールで左端に固定（ヘッダー/ボディ/合計行 共通）。
//
// name 列にだけ position: sticky を当てると、ヘッダー <th> と本文 <td> が
// それぞれ別の合成レイヤーになり、border-collapse 下で列幅の算出が
// 食い違うことがある（ヘッダーと本文で列がズレ、右端に幅のズレ分の
// 隙間や見かけ上の余剰列が出る原因）。
// 幅を固定値で明示し、ヘッダー/本文/合計行で必ず同じ幅になるようにする。
.grid-cell-fixed-col {
  position: -webkit-sticky;
  position: sticky;
  left: 0;
  z-index: 2;
  width: 130px;
  min-width: 130px;
  max-width: 130px;
}

// 左上の交差セル（ヘッダー行 × name 列）: 縦横とも固定し最前面
.grid-cell-fixed-corner {
  z-index: 4;
}

.grid-cell-number {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

// 編集可能セルはホバーで枠ハイライト
.grid-cell-editable {
  cursor: pointer;

  &:hover {
    outline: 2px solid var(--v-primary-base, #9155fd);
    outline-offset: -2px;
  }
}

// 編集中セル: 枠を常時ハイライト
.grid-cell-editing {
  outline: 2px solid var(--v-primary-base, #9155fd);
  outline-offset: -2px;
  // 編集中はセル内の input をぴったり収めるため padding を詰める
  padding: 0;
}

// 未保存セルは下線で示す
.grid-cell-unsaved {
  border-bottom: 2px solid var(--v-primary-base, #9155fd);
}

// インライン編集用の入力欄。セルにぴったり収まるよう装飾を消す。
.grid-cell-input {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  color: inherit;
  padding: 7px 16px;
  box-sizing: border-box;
  text-align: right;
}

.grid-cell-input-number {
  text-align: right;
  font-variant-numeric: tabular-nums;

  // number input のスピンボタン（上下矢印）を非表示にする
  -moz-appearance: textfield; // Firefox

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.grid-empty {
  text-align: center;
  color: rgba(0, 0, 0, .5);
  padding: 24px 16px;
}

.theme--dark .grid-empty {
  color: rgba(231, 227, 252, .5);
}

// ===== 集計中のプログレスバー表示 =====
// セル保存後の依存関係再計算（generateBusinessRuleResult API）の間、
// グリッド上部に細い不確定プログレスバーを表示し「処理中」感を出す。
.grid-loading-indicator {
  position: relative;
  z-index: 6; // ヘッダー行(.grid-head-cell z-index:3) より上
}

// ===== 集計中のセルロック表示 =====
// 集計中（props.loading）は対象シートのセルを操作不可にする。
// 「集計中」の表示はページ上部のグローバルインジケータ（index.vue）と
// 上記のプログレスバーが担う。
// ここではセルがロックされていることを cursor:wait と薄表示で伝える。
// （ステップ／画面遷移はブロックしない。ロックの対象はこのグリッドのセルのみ）
.aggregation-grid--loading .grid-scroll {
  cursor: wait;
}

.aggregation-grid--loading table.grid {
  opacity: .72;
  transition: opacity .15s ease;
}

// 集計中は編集可能セルのホバーアウトラインを出さない
// （クリックしても反応しないため、操作可能に見えると誤解を招く）。
.aggregation-grid--loading .grid-cell-editable:hover {
  outline: none;
}
</style>
